import { useEffect, useState } from 'react'
import { resolveHelpContent } from '@/services/helpContentService'
import { log } from '@/utils/logger'
import type { ResolvedHelpImage } from '@/types/help.types'

export interface FieldHelp {
  loading: boolean
  hasHelp: boolean
  helpText?: string
  helpImages: ResolvedHelpImage[]
  /** The dropdown value this help is specific to, if any — for display as a sub-header. */
  dropdownValueCode?: string | null
}

const NO_HELP: FieldHelp = { loading: false, hasHelp: false, helpImages: [] }

// "Not in List" is a UI sentinel for an explicit non-match, never real domain data —
// it can never have configured help, so skip the network round-trip for it entirely.
const NON_RESOLVABLE_VALUES = new Set(['Not in List'])

// In-session cache so re-selecting a field/value already resolved this session (e.g.
// toggling back and forth between two dropdown options) doesn't re-hit the network.
// Module-level so it's shared across every FieldInput/HelpPanel instance on the page.
const resultCache = new Map<string, FieldHelp>()

function keyFor(fieldId: string | null | undefined, currentValue: string | undefined): string | null {
  return fieldId ? `${fieldId}::${currentValue ?? ''}` : null
}

/** Test-only: clears the in-session resolve cache so tests don't leak state across cases. */
export function __clearFieldHelpCacheForTests(): void {
  resultCache.clear()
}

/**
 * Resolves DB-driven help content for a field, keyed by the field's currently
 * selected/entered value so dropdown-value-level overrides take priority over
 * field-level help. Aborts the in-flight request whenever fieldId/currentValue
 * change, so a dependent-dropdown reset never shows stale help.
 */
export function useFieldHelp(fieldId: string | null | undefined, currentValue: string | undefined): FieldHelp {
  const key = keyFor(fieldId, currentValue)
  const [state, setState] = useState<FieldHelp>(NO_HELP)
  const [stateKey, setStateKey] = useState<string | null>(null)

  // Reset synchronously during render (not via useEffect) whenever fieldId/currentValue
  // change, so no consumer (e.g. HelpPanel's auto-close effect) ever observes a stale
  // "not loading, no help" snapshot left over from a *different* field/value.
  if (key !== stateKey) {
    setStateKey(key)
    if (!key) {
      setState(NO_HELP)
    } else if (currentValue !== undefined && NON_RESOLVABLE_VALUES.has(currentValue)) {
      setState(NO_HELP)
    } else if (resultCache.has(key)) {
      setState(resultCache.get(key)!)
    } else {
      setState({ loading: true, hasHelp: false, helpImages: [] })
    }
  }

  useEffect(() => {
    if (!fieldId || !key) return
    if (currentValue !== undefined && NON_RESOLVABLE_VALUES.has(currentValue)) return
    if (resultCache.has(key)) return

    const controller = new AbortController()

    resolveHelpContent(fieldId, currentValue, controller.signal)
      .then((resolved) => {
        if (controller.signal.aborted) return
        const helpImages = resolved?.helpImages ?? []
        const result: FieldHelp =
          !resolved || !resolved.isActive || (!resolved.helpText && helpImages.length === 0)
            ? NO_HELP
            : {
                loading: false,
                hasHelp: true,
                helpText: resolved.helpText ?? undefined,
                helpImages,
                dropdownValueCode: resolved.dropdownValueCode ?? null,
              }
        resultCache.set(key, result)
        setState(result)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        log.error('useFieldHelp: resolve failed', err instanceof Error ? err : new Error(String(err)), { fieldId })
        // Don't cache failures — a transient network error shouldn't stick for the session.
        setState(NO_HELP)
      })

    return () => controller.abort()
  }, [key, fieldId, currentValue])

  return state
}
